import { uniqBy } from "lodash-es";

import {
  DecisionHistoryFiltersV2,
  DecisionHistoryRecordV2,
  useHistoryDecisionsV2,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import {
  NODE_EXECUTIONS_POLLING_INTERVAL,
  NodeExecutionStatusFilter,
  useGetNodeExecutions,
} from "src/api/testRuns/testRunQueries";
import { DecisionHistoryError, ErroredRow } from "src/api/types";
import { StatusFilterKey } from "src/dataTable/TableUtils";
import { ResultDataAndAuxRowV2 } from "src/dataTable/types";
import { errorMessage } from "src/utils/stringUtils";
import { assertUnreachable } from "src/utils/typeUtils";

const SOURCE_ENTRY_ID_DELIMITER = ":";

export type CustomStatusFilterKey = Extract<
  StatusFilterKey,
  "success" | "failure" | "ignored" | "all"
>;

const parseDecisionError = (
  error: string,
  nodeId: string,
): DecisionHistoryError => {
  try {
    return JSON.parse(error) as DecisionHistoryError;
  } catch {
    return {
      msg: error,
      node_id: nodeId,
      exc_type: "unknown_error",
      loc_type: "node",
      loc: nodeId,
      params: {},
    };
  }
};

export const transformNodeResultsToResultDataRows = (
  nodeResults: ReturnType<typeof useGetNodeExecutions>["data"],
  rowIdToIndex: Record<string, number>,
) => {
  const results: ResultDataAndAuxRowV2[] = [];
  const errors = new Map<number, ErroredRow>();

  const deduplicatedResults = uniqBy(
    nodeResults?.pages.flatMap((page) => page.results) ?? [],
    "decision_metadata.id",
  );

  deduplicatedResults.forEach((result, loopIndex) => {
    const sourceEntryId = result.decision_metadata.source_entry_id ?? "";
    const hasRowIndex = sourceEntryId.includes(SOURCE_ENTRY_ID_DELIMITER);
    const [rowIndexOrRowId] = sourceEntryId.split(SOURCE_ENTRY_ID_DELIMITER);
    const effectiveIndex = hasRowIndex
      ? Number(rowIndexOrRowId)
      : (rowIdToIndex[rowIndexOrRowId] ?? loopIndex);

    if (result.decision_metadata.status_code === "200") {
      results.push({
        index: effectiveIndex,
        type: "success",
        data: result.data,
        auxData: {},
        decisionId: result.decision_metadata.id,
        nodeExecutionMetadata: result.execution_metadata,
      });
      return;
    }

    if (result.decision_metadata.status_code === "202") {
      results.push({
        index: effectiveIndex,
        type: "pending",
        data: result.data,
        decisionId: result.decision_metadata.id,
        nodeExecutionMetadata: result.execution_metadata,
      });
      return;
    }

    if (result.decision_metadata.error) {
      const parsedError = parseDecisionError(
        result.decision_metadata.error,
        result.node_id,
      );

      const isIgnored = result.decision_metadata.status_code === "499";
      const isErroredNode = result.node_id === parsedError.node_id;
      results.push({
        index: effectiveIndex,
        type: isErroredNode ? (isIgnored ? "ignored" : "failure") : "success",
        auxData: {},
        data: result.data,
        errorMessage: errorMessage(
          parsedError.msg ?? result.decision_metadata.error,
        ),
        errorOriginNodeId: parsedError.node_id ?? result.node_id,
        nodeExecutionMetadata: result.execution_metadata,
        decisionId: result.decision_metadata.id,
      });
      errors.set(effectiveIndex, {
        ...parsedError,
        index: effectiveIndex,
        data: result.data,
        aux_data: {},
        error_hash: "",
      });
      return;
    }

    results.push({
      index: effectiveIndex,
      type: "ignored",
      data: result.data,
      auxData: {},
      decisionId: result.decision_metadata.id,
      nodeExecutionMetadata: result.execution_metadata,
    });
  });

  return { dataToDisplay: results, erroredRows: errors };
};

export const transformDecisionToResultDataRow = ({
  decision,
  index,
  nodeId,
}: {
  decision: DecisionHistoryRecordV2;
  nodeId?: string;
  index: number;
}): ResultDataAndAuxRowV2 | null => {
  const decisionId = decision.id;
  const [nodeResults, nodeExecutionMetadata] = [
    decision.node_results,
    decision.node_results.node_execution_metadata,
  ];
  const nodeData = nodeId ? nodeResults.nodes[nodeId]?.data : {};
  const decisionData = decision.is_error
    ? (decision.error?.data ?? {})
    : (decision.response.data ?? {});
  const successResult = {
    index,
    type: "success",
    data: nodeId ? nodeData : decisionData,
    nodeExecutionMetadata,
    auxData: {},
    decisionId,
  } as ResultDataAndAuxRowV2;

  if (nodeId && !(nodeId in nodeResults.nodes)) return null;

  if (decision.is_error && decision.error) {
    if (decision.error.exc_type === "row_ignored") {
      if (nodeId && decision.error.node_id !== nodeId) return successResult;

      return {
        index,
        type: "ignored",
        data: decision.error.data ?? {},
        auxData: {},
        decisionId,
        errorMessage: errorMessage(decision.error.msg),
        errorOriginNodeId: decision.error.node_id,
        nodeExecutionMetadata,
      };
    } else if (decision.error.exc_type === "output_mismatch_error") {
      if (nodeId && decision.error.node_id !== nodeId) return successResult;

      return {
        index,
        type: "success_mismatch",
        data: decision.error.data ?? {},
        expectedOutputKeysMismatch: decision.error.mismatched_keys,
        expectedOutputData: decision.error.expected_output_data,
        auxData: {},
        decisionId,
        nodeExecutionMetadata,
      };
    } else {
      if (nodeId && decision.error.node_id !== nodeId) return successResult;

      return {
        index,
        errorMessage: errorMessage(decision.error.msg),
        type: "failure",
        data: decision.error.data ?? {},
        auxData: {},
        errorOriginNodeId: decision.error.node_id,
        decisionId,
        nodeExecutionMetadata,
      };
    }
  }

  if (!decision.is_error) {
    const isPending = decision.status_code === "202";
    if (isPending) {
      return {
        index,
        type: "pending",
        data: nodeId ? nodeData : decision.response.data,
        nodeExecutionMetadata,
        decisionId,
      };
    } else {
      return successResult;
    }
  }

  return null;
};

export const transformDecisionsToResultDataRows = (
  decisions: ReturnType<typeof useHistoryDecisionsV2>["data"],
  rowIdToIndex: Record<string, number>,
  outputNodeId?: string,
  expectedOutputColumnNames?: string[],
) => {
  const results: ResultDataAndAuxRowV2[] = [];
  const errors = new Map<number, ErroredRow>();

  const deduplicatedDecisions = uniqBy(
    decisions?.pages.flatMap((page) => page.data) ?? [],
    "id",
  );

  deduplicatedDecisions.forEach((decision, index) => {
    const effectiveIndex = decision.source_entry_id
      ? (rowIdToIndex[decision.source_entry_id] ?? index)
      : index;

    const result = transformDecisionToResultDataRow({
      decision,
      index: effectiveIndex,
    });

    if (!result) return;

    // Upgrade plain `success` rows to `success_match` when the BE flagged the
    // output node's expected output as matched. V2 list_decisions doesn't
    // surface source_entry_id, so we can't look up per-row expected values —
    // since actual == expected by definition of "matched", we synthesize
    // expectedOutputData from the decision's own data restricted to the
    // dataset's expected-output columns to drive the green highlight and
    // popover.
    const expectedOutputMatched =
      result.type === "success" &&
      outputNodeId !== undefined &&
      decision.node_results.node_execution_metadata?.[outputNodeId]
        ?.expected_output_matched === true;

    if (expectedOutputMatched && !decision.is_error) {
      const expectedOutputData = Object.fromEntries(
        (expectedOutputColumnNames ?? [])
          .filter((name) => Object.hasOwn(decision.response.data, name))
          .map((name) => [name, decision.response.data[name]]),
      );
      results.push({ ...result, type: "success_match", expectedOutputData });
    } else {
      results.push(result);
    }

    if (result.type === "failure" && decision.error) {
      errors.set(effectiveIndex, {
        data: {},
        aux_data: {},
        ...decision.error,
        index: effectiveIndex,
        error_hash: "",
      });
    }
  });

  return { dataToDisplay: results, erroredRows: errors };
};

export const computeDecisionsRefetchInterval = ({
  data,
  totalRowsCount,
}: {
  data:
    | {
        pages: {
          data: unknown[];
        }[];
      }
    | undefined;
  totalRowsCount: number;
}): { shouldPoll: boolean; interval: number | false } => {
  if (!data) {
    return { shouldPoll: true, interval: NODE_EXECUTIONS_POLLING_INTERVAL };
  }

  const loadedPagesCount = data.pages.length;
  const computedExpectedResultsCount = loadedPagesCount * 100; // based on the page size
  // we cap the expected results count at the total rows count
  const effectiveExpectedResultsCount = Math.min(
    computedExpectedResultsCount,
    totalRowsCount,
  );
  const resultsCount = data.pages.reduce(
    (acc, page) => acc + page.data.length,
    0,
  );

  // if two pages are loaded we only expect 200 rows (based on the page size)
  // but if less rows are loaded than expected then we keep polling
  if (resultsCount >= effectiveExpectedResultsCount) {
    return { shouldPoll: false, interval: false };
  }

  return { shouldPoll: true, interval: NODE_EXECUTIONS_POLLING_INTERVAL };
};

export const toDecisionStatusFilter = (
  statusFilter: CustomStatusFilterKey,
): DecisionHistoryFiltersV2["fields"]["status"] => {
  switch (statusFilter) {
    case "success":
      return "success";
    // Unable to filter on ignored as failure already includes ignored for decision history
    // disabled ignored filter for output node, added here for completeness
    case "failure":
    case "ignored":
      return "failure";
    case "all":
      return undefined;
    default:
      return assertUnreachable(statusFilter);
  }
};

export const toNodeExecutionStatusFilter = (
  statusFilter: CustomStatusFilterKey,
): NodeExecutionStatusFilter[] | undefined => {
  switch (statusFilter) {
    case "success":
      return ["successful"] as const;
    case "failure":
      return ["failed"] as const;
    case "ignored":
      return ["ignored"] as const;
    case "all":
      return undefined;
    default:
      return assertUnreachable(statusFilter);
  }
};
